import Addbox from "@mui/icons-material/AddBox";
import EditRounded from "@mui/icons-material/EditRounded";
import Remove from "@mui/icons-material/Remove";
import { Card, CardActions, CardContent, Grid, Theme, Typography, useTheme, Button } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { INewTemplateFlight } from "@vmosolutions/oc-interfaces";
import classNames from "classnames";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { CheckboxField } from "src/components/FormikComponents/CheckboxField";
import { IRootState } from "src/redux/reducers/CombinedReducers";
import { IStructuresState } from "src/redux/reducers/StructuresReducer";
import { AlertModel } from "src/redux/state/Alert";
import { updateAirportOptions, findAirports } from "src/utils/airportHelper";
import { EventTypes } from "../FlightBuilder";
import { FlightRowDisplay } from "./FlightRowDisplay";
import { FlightRowEdit } from "./FlightRowEdit";
import { IAirportOptionMap, IAirportOption } from "src/redux/reducers/StructuresReducer";
import { addAirportToList } from "src/redux/actions/Structure.Actions";

interface IFlightRowsProps {
  mosaicId: string;
  flightLegs: INewTemplateFlight[];
  legsAreDirty: boolean;
  resetAllChangesToLegs: () => void;
  onSubmit: (flightValues: INewTemplateFlight) => void;
  addFlightLeg: () => void;
  removeFlightLeg: (id: string) => void;
  eventType?: EventTypes;
  selectedRowIndex?: number;
  buildAsLocal: boolean;
  setBuildAsLocal: (state: boolean) => void;
  startDate: number;
  setDialogData?: (state: any) => any;
}

const formActionStyles = (theme: Theme): any => ({
  marginTop: "10px",
  "& .form_button": {
    width: "auto",
    height: 30,
    fontSize: 11,
    lineHeight: "12px",
    padding: "2px 8px",
    margin: "0 2px",
  },
});

const gridContainerStyles = (theme: Theme): any => ({
  width: "100%",
  height: "100%",
  display: "flex",
  alignContent: "flex-start",
  position: "relative",
  padding: "10px",
  flexGrow: 1,
  "& .edit_icon": {
    width: 16,
    height: 16,
    border: `1px solid ${theme.palette.primary.contrastText}`,
    color: theme.palette.primary.contrastText,
    borderRadius: "3px",
    "&.active": {
      border: `1px solid ${theme.custom.forms.dirtyColor}`,
      color: theme.custom.forms.dirtyColor,
    },
  },
  "& .dialog_spinner_progress": { margin: -10 },
  "& .field-group": {
    flexDirection: "row",
    width: 380,
  },
  "& .formik_field": {
    width: 363,
    "&.note, &.comment": { width: 736 },
  },
  "& .error-message": { fontSize: 10, margin: 0 },
  "& p.required_fields_label, & p.optional_fields_label, & .crewslot_fields_label": {
    margin: 0,
    marginLeft: "74px",
    fontWeight: 300,
    fontSize: 10,
    textTransform: "uppercase",
    color: theme.custom.forms.labels,
  },
  "& p.optional_fields_label": {
    marginLeft: "672px",
  },
  "& .checkbox": { width: 36 },
  "& .flight_row": {
    width: "auto",
    borderTop: `1px solid ${theme.palette.background.default}`,
  },
  "& .required_fields": {
    minWidth: 750,
  },
  "& .required_fields, & .crewslot_fields": {
    backgroundColor: alpha(theme.custom.forms.labels, 0.2),
    margin: "2px 0px",
    width: "auto",
    "&.invalid": {
      backgroundColor: alpha(theme.custom.forms.errorColor, 0.2),
    },
  },
  "& .optional_fields": {
    margin: "2px 0px",
    width: "auto",
  },
  "& .flight_leg": {
    textAlign: "right",
    width: 22,
    lineHeight: "24px",
    fontSize: 14,
    margin: "5px",
    padding: "0px 5px",
  },
  "& .flight_number": {
    width: 100,
    margin: "5px",
    "& label": { display: "none" },
    "& div.input_control": {
      borderRadius: "3px",
      "& div.input-base": {
        borderRadius: "3px",
      },
    },
    "&.type_assigned": {
      "& div.input-base": {
        backgroundColor: "inherit",
        color: "inherit !important",
        "& input": { color: "inherit !important" },
      },
    },
  },
  "& .mock_event_chip": {
    margin: "5px",
    height: 24,
    width: 100,
    padding: "2px 8px",
    borderRadius: "3px",
    fontWeight: 600,
    textAlign: "center",
  },
  "& .event_type": {
    "&.type_assigned": {
      "& div.input-base": {
        backgroundColor: "inherit",
        color: "inherit !important",
        "& input": { color: "inherit !important" },
      },
    },
  },
  "& .location, .origin, .destination, .flight_type, .event_type": {
    width: 100,
    "& .input_control": {
      width: "100%",
      "& .react-select-container": {
        width: "100%",
        "& .react-select__control ": { height: 24 },
      },
    },
  },
  "& .aircraft_config": {
    minWidth: 140,
    maxWidth: 160,
    width: "auto",
  },
  "& .aircraft ": {
    marginLeft: "10px",
    minWidth: 140,
    maxWidth: 160,
    width: "auto",
    "& .input_control": {
      width: "100%",
      "& .react-select-container": {
        width: "100%",
        "& .react-select__control ": { height: 24 },
      },
    },
  },
  "& .flags, & .customer": {
    width: 344,
    marginLeft: "23px",
    marginTop: "10px",
    "& .input_control": {
      width: "100%",
      "& .react-select-container": {
        width: "100%",
        "& .react-select__control ": { height: 24 },
      },
    },
  },
  "& .schedule_out, & .schedule_in": {
    width: 162,
  },
  "& .recurrent_schedule_out, & .recurrent_schedule_in": { width: 62 },
  "& .block_duration": {
    width: 55,
    opacity: 0.5,
    margin: "5px 10px 5px 5px",
    padding: "2px 8px",
  },
  "& .day_offset": {
    width: "auto",
    "& label": { display: "none" },
  },
  "& .oc_datetime": {
    // used in flight
    alignItems: "flex-start",
  },
  "& .scheduled_start, & .scheduled_end": {
    width: 163,
  },
  "& .scheduled_out, & .scheduled_in": {
    // used in recurring flight
    width: "auto",
    alignItems: "flex-start",
    "& .react-datepicker-wrapper": { display: "none" },
    "& .input_control": {
      "& label": { width: 80 },
      "& .time-input": { width: 70, margin: 0 },
    },
  },
  "& .position_count": {
    width: "auto",
    "& label": { width: "auto" },
    "& .input_control": {
      "& div.input-base": {
        maxWidth: 50,
      },
    },
  },
  // Flight status
  "& .status_message": {
    display: "flex",
    alignItems: "center",
    padding: "2px 5px",
    maxWidth: 300,
    fontSize: 12,
    lineHeight: "12px",
    fontWeight: 300,
    color: theme.palette.primary.contrastText,
    "& .flightchip, & .eventchip": {
      margin: "0px 6px ",
      "&:hover": { width: 120 },
    },
    "& .status_arrow": {
      color: alpha(theme.palette.background.paper, 0.5),
    },
    "& .conflict_icon": {
      margin: "0px 5px",
    },
    "& .status_icon": {
      display: "flex",
      "& button": {
        backgroundColor: alpha(theme.palette.background.paper, 0.5),
        borderRadius: "3px",
        padding: 0,
        margin: "0px 5px",
        width: 24,
        height: 24,
        overflow: "hidden",
      },

      "& .icon_foreground": {
        width: "100%",
        marginTop: "1px",
        // Active State - blue
        "&.pending": {
          marginTop: "3px",
          "& path": {
            fill: `${theme.palette.primary.main} !important`,
          },
        },
        // Conflict State - orange
        "&.conflict > path": {
          fill: `${theme.palette.secondary.main} !important`,
        },
        // Error State - red
        "&.failed > path": {
          fill: `${theme.palette.error.main} !important`,
        },
        // Success State - green
        "&.success": {
          marginTop: "3px",
          "& path, & polygon": {
            fill: `${theme.palette.success.main} !important`,
          },
        },
      },
    },
  },
});

/**
 * Base Flight Form
 * @param props -
 */
export const FlightRows = (props: IFlightRowsProps): JSX.Element => {
  const {
    mosaicId,
    flightLegs,
    legsAreDirty,
    resetAllChangesToLegs,
    onSubmit,
    addFlightLeg,
    removeFlightLeg,
    eventType,
    buildAsLocal,
    setBuildAsLocal,
    startDate,
    setDialogData,
  } = props;

  const mosaicPane = useMemo(() => document.getElementById(`${mosaicId}-panel`), [mosaicId]);
  const theme = useTheme();
  const [editRow, setEditRow] = useState<number>(0);
  const [focusField, setFocusField] = useState<string>(null);
  const [shouldLoadPreview, setShouldLoadPreview] = useState<boolean>(false);
  const [missingAirports, setMissingAirports] = useState<any[]>([]);
  const structures: IStructuresState = useSelector((state: IRootState) => state.structures);
  const { airportOptions } = structures;
  const dispatch = useDispatch();

  useEffect(() => {
    async function checkFlights(): Promise<void> {
      if (flightLegs) {
        const promises: Promise<void>[] = flightLegs.map(async (flightLeg) => {
          const updateAirport = async (location: string): Promise<void> => {
            if (location && !airportOptions[location]) {
              setMissingAirports((prev) => ({ ...prev, [location]: true }));

              const resp = await findAirports(location);
              if (resp.length > 0) {
                await updateAirportOptions(location, airportOptions, dispatch);
                setMissingAirports((prev) => ({ ...prev, [location]: false }));
              }
            }
          };

          await updateAirport(flightLeg.destination);
          await updateAirport(flightLeg.origin);
        });

        await Promise.all(promises);
      }
    }

    checkFlights().then(() => {
      setShouldLoadPreview(true);
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flightLegs, airportOptions]);

  const handleAddNewLeg = useCallback(
    (lastRow: boolean): void => {
      if (lastRow) {
        addFlightLeg();
      }
      setEditRow((prev) => prev + 1);
      setFocusField("flightNumber_input");
    },
    [addFlightLeg]
  );

  const watchOnTimeModeChange = useCallback(
    (newIsLocal: boolean): void => {
      // If recurring flight, warn the user, otherwise just change the mode.
      if (eventType === "RecurrentFlight") {
        const dialogData = new AlertModel({
          title: "Warning: Time Assignment Mode Changed?",
          message:
            "Changing between Local and UTC time could potentially shift flight times or durations, especially when building across a DST boundary. Please verify flight times after proceeding.",
          buttons: [
            {
              label: "Cancel",
              handler: () => {
                setDialogData(null);
              },
            },
            {
              label: `${newIsLocal ? "Change to LOCAL" : "Change to UTC"}`,
              handler: () => {
                setBuildAsLocal(newIsLocal);
                setDialogData(null);
              },
            },
          ],
        });
        setDialogData(dialogData);
      } else {
        setBuildAsLocal(newIsLocal);
      }
    },
    [eventType, setBuildAsLocal, setDialogData]
  );
  return (
    <Card classes={{ root: classNames("flights_form_card") }}>
      <CardContent className="card_content" style={{ padding: 10, minWidth: 1150 }}>
        <Grid container direction="column">
          <Grid sx={{ ...gridContainerStyles(theme), padding: 0 }} container className={"form_body"}>
            <Typography className="required_fields_label">required fields</Typography>
            <Typography className="optional_fields_label">optional fields</Typography>

            <Grid container direction="row" sx={{ marginTop: "5px" }} flexWrap="nowrap">
              <EditRounded className={`edit_icon ${legsAreDirty ? "active" : ""}`} sx={{ marginLeft: "15px" }} />
              <Typography variant="caption" sx={{ marginLeft: "10px" }}>
                Leg
              </Typography>
              <Typography variant="caption" sx={{ marginLeft: "15px" }}>
                Flight Number
              </Typography>
              <Typography variant="caption" sx={{ marginLeft: "28px" }}>
                Origin
              </Typography>
              <Typography variant="caption" sx={{ marginLeft: "76px" }}>
                Destination
              </Typography>
              <Typography variant="caption" sx={{ marginLeft: "45px" }}>
                Flight Type
              </Typography>
              <Typography variant="caption" sx={{ marginLeft: "45px" }}>
                Day Offset
              </Typography>
              <Typography variant="caption" sx={{ marginLeft: "13px" }}>
                Sched Out
              </Typography>
              <Typography variant="caption" sx={{ marginLeft: "25px" }}>
                Sched In
              </Typography>
              <Typography variant="caption" sx={{ marginLeft: "32px" }}>
                Duration
              </Typography>
              <Typography variant="caption" sx={{ marginLeft: "30px" }}>
                Aircraft
              </Typography>
              <Typography variant="caption" sx={{ marginLeft: "110px" }}>
                AC Config
              </Typography>
            </Grid>

            <Grid container direction="column">
              {shouldLoadPreview && (
                <>
                  {flightLegs.map((flight: INewTemplateFlight, legIndex: number) => {
                    return legIndex === editRow && flight?._id?.includes("temp") ? (
                      <Grid item key={`flight_leg_${legIndex}`}>
                        <FlightRowEdit
                          initialValues={flight}
                          legIndex={legIndex}
                          legCount={flightLegs.length}
                          mosaicPane={mosaicPane}
                          onSubmit={onSubmit}
                          theme={theme}
                          eventType={eventType}
                          buildAsLocal={buildAsLocal}
                          setEditRow={setEditRow}
                          startDate={startDate}
                          focusField={focusField}
                          addNewLeg={handleAddNewLeg}
                        />
                      </Grid>
                    ) : (
                      <Grid item key={`flight_leg_${legIndex}`}>
                        <FlightRowDisplay
                          flight={flight}
                          legIndex={legIndex}
                          mosaicPane={mosaicPane}
                          onSubmit={onSubmit}
                          theme={theme}
                          eventType={eventType}
                          buildAsLocal={buildAsLocal}
                          setEditRow={setEditRow}
                          startDate={startDate}
                          setFocusField={setFocusField}
                        />
                      </Grid>
                    );
                  })}
                </>
              )}
            </Grid>
          </Grid>
        </Grid>

        <CardActions sx={formActionStyles(theme)}>
          <Button
            id={`${mosaicId}_resetFlights_btn`}
            className="form_button"
            variant="contained"
            color="secondary"
            disabled={!legsAreDirty}
            //type="button"
            onClick={() => resetAllChangesToLegs()}
          >
            Reset
          </Button>
          <Button
            id={`${mosaicId}_removeFlight_btn`}
            className="form_button"
            variant="contained"
            color="secondary"
            disabled={flightLegs?.length === 0 || editRow === -1}
            //type="button"
            onClick={() => removeFlightLeg(flightLegs[editRow]?._id)}
          >
            <Remove style={{ marginRight: 5 }} />
            Remove Leg
          </Button>
          <Button
            id={`${mosaicId}_addFlight_btn`}
            className="form_button"
            variant="contained"
            color="primary"
            onClick={() => addFlightLeg()}
          >
            <Addbox style={{ marginRight: 5 }} />
            Add New Leg
          </Button>

          <Grid item sx={{ marginTop: "-5px", marginLeft: "10px", width: "auto" }}>
            <CheckboxField
              label={"Flight times in Local:"}
              labelPlacement="start"
              checked={buildAsLocal}
              onChange={() => watchOnTimeModeChange(!buildAsLocal)}
            />
          </Grid>
        </CardActions>
      </CardContent>
    </Card>
  );
};
